import { useRef, useCallback, useEffect } from 'react';
import { Send, Loader2, MessageCircle, User, Music, ArrowLeft, DollarSign } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { MessageBubble } from './MessageBubble';
import { api } from '../../lib/api';

export function MessageThread({
  selectedConversation, isArtistView,
  messages, loadingMessages, messagesEndRef,
  newMessage, setNewMessage, handleSendMessage, sending,
  handleBackToList,
  handleAcceptQuote, handleDeclineQuote, processingQuoteId,
  handleDeleteMessage,
  setQuotePrice, setQuoteDescription, setQuoteBuyerMessage, setQuoteDeliveryDays,
  setQuoteDialogOpen,
  peerTyping,
}) {
  const typingSentAtRef = useRef(0);
  const stopTypingTimerRef = useRef(null);
  const convIdRef = useRef(selectedConversation?.id);

  useEffect(() => { convIdRef.current = selectedConversation?.id; }, [selectedConversation?.id]);

  // Clean up any pending "stop typing" when leaving the conversation.
  useEffect(() => () => {
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
  }, []);

  const notifyTyping = useCallback(() => {
    const convId = convIdRef.current;
    if (!convId) return;
    const now = Date.now();
    // Server also throttles, but we throttle client-side to avoid noise
    if (now - typingSentAtRef.current > 1800) {
      typingSentAtRef.current = now;
      api.sendTyping(convId, true).catch(() => {});
    }
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    stopTypingTimerRef.current = setTimeout(() => {
      typingSentAtRef.current = 0;
      api.sendTyping(convId, false).catch(() => {});
    }, 3500);
  }, []);

  if (!selectedConversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#0a0a0a]">
        <div className="text-center">
          <MessageCircle className="w-16 h-16 mx-auto mb-4 text-[#222]" />
          <p className="text-[#555] text-sm">Select a conversation</p>
        </div>
      </div>
    );
  }

  const headerName = isArtistView ? selectedConversation.user_name : selectedConversation.artist_name;
  const headerSub = isArtistView ? 'Customer' : 'Artist';

  const openQuoteDialog = () => {
    setQuotePrice(''); setQuoteDescription(''); setQuoteBuyerMessage(''); setQuoteDeliveryDays('7');
    setQuoteDialogOpen(true);
  };

  return (
    <div className="flex-1 flex flex-col min-w-0">
      <div className="flex items-center gap-3 p-3 border-b border-[#262626] bg-surface flex-shrink-0">
        <Button variant="ghost" size="icon" onClick={handleBackToList} className="lg:hidden flex-shrink-0">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="w-9 h-9 rounded-full overflow-hidden bg-[#1a1a1a] flex-shrink-0">
          {(!isArtistView && selectedConversation.artist_image) ? (
            <img src={selectedConversation.artist_image} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {isArtistView ? <User className="w-4 h-4 text-[#404040]" /> : <Music className="w-4 h-4 text-[#404040]" />}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-sm truncate">{headerName}</h3>
          <p className="text-xs text-[#666]">{headerSub}</p>
        </div>
        {isArtistView && (
          <Button onClick={openQuoteDialog} className="btn-primary text-xs px-3 py-1 h-auto" data-testid="send-quote-btn">
            <DollarSign className="w-3 h-3 mr-1" />Quote
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 p-4 bg-[#0a0a0a]">
        {loadingMessages ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-lime" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8 text-[#666]">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No messages yet. Start the conversation!</p>
          </div>
        ) : messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            isArtistView={isArtistView}
            processingQuoteId={processingQuoteId}
            onAcceptQuote={handleAcceptQuote}
            onDeclineQuote={handleDeclineQuote}
            onDeleteMessage={handleDeleteMessage}
          />
        ))}
        {peerTyping && (
          <div className="flex items-center gap-2 text-xs text-[#888]" data-testid="peer-typing-indicator">
            <span className="inline-flex gap-0.5">
              <span className="w-1.5 h-1.5 bg-lime rounded-full animate-pulse [animation-delay:0ms]" />
              <span className="w-1.5 h-1.5 bg-lime rounded-full animate-pulse [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 bg-lime rounded-full animate-pulse [animation-delay:300ms]" />
            </span>
            {headerName} is typing…
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className="flex gap-2 p-3 border-t border-[#262626] bg-surface flex-shrink-0">
        <Textarea
          value={newMessage}
          onChange={(e) => { setNewMessage(e.target.value); if (e.target.value.trim()) notifyTyping(); }}
          placeholder="Type your message..."
          className="input-dark min-h-[44px] max-h-[80px] resize-none flex-1 text-sm"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(e); } }}
          data-testid="inbox-message-input"
        />
        <Button type="submit" disabled={sending || !newMessage.trim()} className="btn-primary px-3 self-end" data-testid="inbox-send-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
}
